import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { BsQrCodeScan } from "react-icons/bs";
import { RiDeleteBin6Line } from "react-icons/ri";
import { ClipLoader } from "react-spinners";
import { useDebounce } from "use-debounce";
import { Scanner } from "@yudiel/react-qr-scanner";
import { API } from "../../../constants/api";
import { coins, getNetworkLabel } from "../../../constants/constants";
import { errorMessage } from "../../../utils/errorMessage";
import useAuthAxios from "../../../utils/baseAxios";
import CopyToClipboard from "../../../components/utils/CopyToClipboard";
import { darkNoTransaction, noTransaction } from "../../../assets/images";
import { useUser } from "../../../context/user-context";
import { validateWalletAddress } from "../../../utils/validateWallet";

const CryproAddresses = () => {
  const { theme } = useUser();
  const axiosInstance = useAuthAxios();
  const queryClient = useQueryClient();

  const [openAdd, setOpenAdd] = useState(false);
  const [scan, setScan] = useState(false);
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [beneficiaryNotes, setBeneficiaryNotes] = useState("");
  const [address, setAddress] = useState("");
  const [network, setNetwork] = useState("");
  const [selectedCoin, setSelectedCoin] = useState<any>(null);
  const [isValidAddress, setIsValidAddress] = useState(false);

  const resetAddForm = () => {
    setBeneficiaryName("");
    setBeneficiaryNotes("");
    setAddress("");
    setNetwork("");
    setSelectedCoin(null);
    setIsValidAddress(false);
    setScan(false);
  };

  const applyDefaultCoinAndNetwork = () => {
    const first = coins[0];
    if (!first) {
      setSelectedCoin(null);
      setNetwork("");
      return;
    }
    setSelectedCoin(first);
    setNetwork(first.networks?.[0]?.name ?? "");
  };

  const openAddModal = () => {
    setBeneficiaryName("");
    setBeneficiaryNotes("");
    setAddress("");
    setIsValidAddress(false);
    setScan(false);
    applyDefaultCoinAndNetwork();
    setOpenAdd(true);
  };

  const [debouncedAddress] = useDebounce(address, 800);

  const validateAddress = useCallback(() => {
    if (
      debouncedAddress === undefined ||
      debouncedAddress.trim() === ""
    ) {
      return false;
    }

    if (network && selectedCoin) {
      const isValid = validateWalletAddress(
        debouncedAddress,
        selectedCoin.abbr,
        network
      );

      if (!isValid) {
        toast.error(
          `Invalid ${selectedCoin.abbr} address for the selected network.`
        );
      }

      return isValid;
    }
    return false;
  }, [debouncedAddress, selectedCoin, network]);

  useEffect(() => {
    const isValid = validateAddress();
    setIsValidAddress(isValid);
  }, [validateAddress]);
  //getbeneficiaries
  const getAllBeneficiaries = async () => {
    const response = await axiosInstance.get(API.getBeneficiaries);
    return response.data;
  };

  const { data: allBeneficiaries, error: error2 } = useQuery({
    queryKey: ["all-beneficiaries"],
    queryFn: getAllBeneficiaries,
    retry: 1,
  });
  useEffect(() => {
    if (error2) {
      const newError = error2 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error2]);

  // delete beneficiary
  const deleteBeneficiary = async (id: string) => {
    const response = await axiosInstance.delete(API.deleteBeneficiary(id));
    return response.data;
  };
  const completeDeleteBeneficiary = useMutation({
    mutationFn: deleteBeneficiary,
    onSuccess: (r) => {
      toast.success(r.message || "Deleted");
      queryClient.invalidateQueries({ queryKey: ["all-beneficiaries"] });
    },
    onError: (e) => {
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });

  const saveBeneficiaryApi = async (data: {
    name: string;
    notes: string;
    crypto: { address: string; network: string; currency: string };
  }) => {
    const response = await axiosInstance.post(API.saveBeneficiary, data);
    return response.data;
  };

  const completeSaveBeneficiary = useMutation({
    mutationFn: saveBeneficiaryApi,
    onSuccess: (r) => {
      toast.success(r.message || "Saved");
      queryClient.invalidateQueries({ queryKey: ["all-beneficiaries"] });
      setOpenAdd(false);
      resetAddForm();
    },
    onError: (e) => {
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });

  const items = allBeneficiaries?.data?.items || [];
  const containerClass =
    items.length > 2 ? " w-full h-[400px] overflow-auto" : "mt-2 w-full";

  const handleCoinChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedAbbr = e.target.value;
    const coin = coins.find((c: any) => c.abbr === selectedAbbr) ?? null;
    setSelectedCoin(coin);
    setNetwork(coin?.networks?.[0]?.name ?? "");
  };

  const handleScan = (data: any) => {
    if (data) {
      setAddress(data[0].rawValue);
      setScan(false);
    }
  };

  const handleScanError = (err: unknown) => {
    console.error("QR Scanner Error: ", err);
    const message = err instanceof Error ? err.message : "Scanner error";
    toast.error(message);
  };

  const saveDisabled =
    !beneficiaryName.trim() ||
    !selectedCoin ||
    !network ||
    !address.trim() ||
    !isValidAddress ||
    completeSaveBeneficiary.isPending;

  return (
    <div className="w-full mt-4">
      <div className={containerClass}>
        {!items.length ? (
          <div className="flex flex-col justify-center mt-10 items-center">
            {theme === "dark" ? (
              <img src={darkNoTransaction} alt="" />
            ) : (
              <img src={noTransaction} alt="" />
            )}
            <h4 className="font-bold text-[#869AA9]  pt-2">
              No saved crypto addresses
            </h4>
            <p className=" text-[#869AA9] pt-2 text-[12px]">
              All your saved crypto addresses will show up here
            </p>
          </div>
        ) : (
          <div className="w-full  py-2">
            <div className="flex flex-col gap-3">
              {items.map((item: any) => (
                <div
                  key={item._id}
                  className="flex justify-between py-3 border rounded-xl px-3 border-gray-200 dark:border-gray-600 items-start"
                >
                  <button className="text-left w-full pr-3" onClick={() => {}}>
                    <h4 className="text-[#13181B] dark:text-gray-100 font-semibold font-sora text-[14px]">
                      {item.name}
                    </h4>
                    {item.notes && (
                      <p className="text-gray-500  mt-1 text-[12px]">
                        {item.notes}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <CopyToClipboard text={item?.crypto?.address}>
                        <span className="text-[12px] font-medium text-gray-700 dark:text-gray-300">
                          {item?.crypto?.address?.slice(0, 6)}...
                          {item?.crypto?.address?.slice(-6)}
                        </span>
                      </CopyToClipboard>
                      {item?.crypto?.network && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-[#E8F3FC] text-secondary_button uppercase">
                          {item.crypto.network}
                        </span>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => completeDeleteBeneficiary.mutate(item._id)}
                    className="text-red-500 ml-2"
                    aria-label="Delete beneficiary"
                  >
                    {completeDeleteBeneficiary.isPending ? (
                      <ClipLoader color="#ef4444" size={18} />
                    ) : (
                      <RiDeleteBin6Line className="text-[18px]" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={openAddModal}
        className="mt-8 bg-secondary_button text-white font-medium text-[14px] h-[48px] rounded-xl w-full"
      >
        Add wallet address
      </button>

      {openAdd && (
        <div className="fixed inset-0 z-[100] flex font-manrope justify-start items-start pt-16 overflow-auto pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
          <div className="w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white rounded-xl mx-auto p-6 dark:bg-[#192025] lgss:w-2/5 xxl:w-1/3">
            <div className="w-full flex justify-between items-center mb-4">
              <h4 className="text-gray-900 dark:text-gray-100 font-sora font-semibold text-[18px]">
                Add wallet address
              </h4>
              <button
                type="button"
                onClick={() => {
                  setOpenAdd(false);
                  resetAddForm();
                }}
                className="text-[13px] text-secondary_button"
              >
                Close
              </button>
            </div>

            <div className="mt-2">
              <label className="text-gray-700 dark:text-gray-300 text-[12px] font-semibold">
                Coin
              </label>
              <div className="h-[48px] mt-2 rounded-xl border border-gray-300 dark:border-gray-600 px-2 flex gap-1 justify-start items-center">
                <div className="flex justify-center items-center w-[20px] h-[20px] rounded-full overflow-hidden">
                  {selectedCoin?.logo ? (
                    <img
                      src={selectedCoin.logo}
                      className="w-full h-full object-cover"
                      alt=""
                    />
                  ) : null}
                </div>
                <select
                  value={selectedCoin?.abbr || ""}
                  onChange={handleCoinChange}
                  className="outline-none bg-transparent w-full text-[14px] text-gray-800 dark:text-gray-200"
                >
                  {coins.map((coin: any, index: number) => (
                    <option key={index} value={coin.abbr}>
                      {coin.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-gray-700 dark:text-gray-300 text-[12px] font-semibold">
                Network
              </label>
              <div className="h-[48px] mt-2 rounded-xl border border-gray-300 dark:border-gray-600 px-2 flex gap-1 justify-start items-center">
                <select
                  value={network}
                  onChange={(e) => setNetwork(e.target.value)}
                  className="outline-none w-full bg-transparent text-[14px] text-gray-800 dark:text-gray-200"
                  disabled={!selectedCoin?.networks?.length}
                >
                  {selectedCoin?.networks?.map((n: any, index: number) => (
                    <option key={index} value={n.name}>
                      {getNetworkLabel(n.name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-gray-700 dark:text-gray-300 text-[12px] font-semibold">
                Address
              </label>
              <div className="flex justify-between text-[13px] mt-2 text-gray-800 dark:text-gray-200 items-center border bg-transparent border-gray-200 dark:border-gray-600 rounded-xl px-4 h-[52px]">
                <input
                  type="text"
                  placeholder={
                    selectedCoin
                      ? `Paste ${selectedCoin.abbr} address`
                      : "Select a coin first"
                  }
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={!selectedCoin || !network}
                  className="text-gray-700 dark:text-gray-300 bg-transparent text-[12px] w-11/12 text-left outline-none disabled:opacity-50"
                />
                <BsQrCodeScan
                  className={`text-secondary_button text-[20px] shrink-0 ${
                    !selectedCoin || !network
                      ? "opacity-40 pointer-events-none"
                      : "cursor-pointer"
                  }`}
                  onClick={() => {
                    if (selectedCoin && network) setScan(true);
                  }}
                />
              </div>
            </div>

            {scan && (
              <div className="fixed inset-0 flex px-4 sm:px-6 md:px-20 lg:px-28 xl:px-40 2xl:px-60 items-start pt-32 sm:pt-40 md:pt-48 justify-center bg-black bg-opacity-70 z-[110]">
                <div className="w-full max-w-md">
                  <Scanner
                    scanDelay={300}
                    onError={handleScanError}
                    onScan={handleScan}
                    styles={{
                      container: {
                        width: "100%",
                        height: "auto",
                        maxHeight: "80vh",
                      },
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setScan(false)}
                  className="absolute top-4 right-4 bg-red-500 text-white px-5 py-3 rounded text-sm sm:text-base"
                  aria-label="Close QR Scanner"
                >
                  Close
                </button>
              </div>
            )}

            <div className="mt-4">
              <label className="text-gray-700 dark:text-gray-300 text-[12px] font-semibold">
                Name
              </label>
              <input
                type="text"
                value={beneficiaryName}
                onChange={(e) => setBeneficiaryName(e.target.value)}
                placeholder="Label this address"
                className="w-full h-[48px] mt-2 outline-none text-[13px] border border-gray-300 dark:border-gray-600 bg-[#F7F8F9] dark:bg-[#1F2B36] dark:text-gray-300 text-gray-700 px-4 rounded-xl"
              />
            </div>

            <div className="mt-4">
              <label className="text-gray-700 dark:text-gray-300 text-[13px] font-medium">
                Notes <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                value={beneficiaryNotes}
                onChange={(e) => setBeneficiaryNotes(e.target.value)}
                placeholder="Add a short note"
                className="w-full min-h-[80px] mt-2 outline-none text-[13px] border border-gray-300 dark:border-gray-600 bg-[#F7F8F9] dark:bg-[#1F2B36] dark:text-gray-300 text-gray-700 px-4 py-3 rounded-xl resize-none"
              />
            </div>

            <button
              type="button"
              onClick={() => {
                if (!selectedCoin || !network) return;
                completeSaveBeneficiary.mutate({
                  name: beneficiaryName.trim(),
                  notes: beneficiaryNotes,
                  crypto: {
                    address: address.trim(),
                    network: network.toUpperCase(),
                    currency: selectedCoin.abbr,
                  },
                });
              }}
              disabled={saveDisabled}
              className={
                saveDisabled
                  ? "mt-6 w-full h-[48px] flex justify-center items-center font-manrope mx-auto text-gray-200 rounded-xl bg-gray-400 dark:bg-[#8f9294]"
                  : "mt-6 w-full h-[48px] flex justify-center items-center font-manrope mx-auto font-medium rounded-xl text-white bg-secondary_button"
              }
            >
              {completeSaveBeneficiary.isPending ? (
                <ClipLoader color="#ffffff" size={20} />
              ) : (
                "Save address"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CryproAddresses;
